import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	Modal,
	Stack,
	Switch,
	Text,
	Textarea,
	TextInput,
} from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	displayPartnerDomain,
	toPartnerApiDomain,
} from "@/features/partners/normalize-partner-domain";
import {
	emptyPartnerEditorValues,
	type PartnerEditorValues,
	partnerEditorSchema,
} from "@/features/partners/partner-schema";
import type { CorporatePartner } from "@/features/partners/types";

export type PartnerEditor =
	| { mode: "create" }
	| { mode: "edit"; row: CorporatePartner };

export type PartnerPendingDelete =
	| { kind: "partner"; id: string; name: string }
	| { kind: "domain"; id: string; name: string };

export function partnerDomainOwnerId(
	editor: PartnerEditor | null,
): string | null {
	if (editor?.mode === "edit") {
		return editor.row.id;
	}
	return null;
}

function defaultsFor(
	editor: PartnerEditor | null,
	editingRow: CorporatePartner | null,
): PartnerEditorValues {
	if (editor?.mode === "edit" && editingRow) {
		return {
			name: editingRow.name,
			domainsText: "",
			isActive: editingRow.isActive,
			domainDraft: "",
		};
	}
	return emptyPartnerEditorValues();
}

export function PartnerEditorModal({
	editor,
	editingRow,
	saving,
	domainPending,
	onAddDomain,
	onRequestDeleteDomain,
	onRequestDeletePartner,
	onClose,
	onSave,
}: {
	editor: PartnerEditor | null;
	editingRow: CorporatePartner | null;
	saving: boolean;
	domainPending: boolean;
	onAddDomain: (domain: string) => void;
	onRequestDeleteDomain: (id: string, name: string) => void;
	onRequestDeletePartner: (id: string, name: string) => void;
	onClose: () => void;
	onSave: (values: PartnerEditorValues) => void;
}) {
	const { t } = useTranslation();
	const { control, handleSubmit, watch, setValue } =
		useForm<PartnerEditorValues>({
			resolver: zodResolver(partnerEditorSchema),
			defaultValues: defaultsFor(editor, editingRow),
		});
	const domainDraft = watch("domainDraft");

	return (
		<Modal
			opened={Boolean(editor)}
			onClose={onClose}
			title={
				editor?.mode === "edit"
					? t("partners.editPartner")
					: t("partners.addPartner")
			}
			centered
			size="lg"
		>
			<form onSubmit={handleSubmit(onSave)}>
				<Stack>
					<Controller
						name="name"
						control={control}
						render={({ field, fieldState }) => (
							<TextInput
								{...field}
								label={t("partners.partnerName")}
								error={fieldState.error?.message}
							/>
						)}
					/>
					{editor?.mode === "create" ? (
						<Controller
							name="domainsText"
							control={control}
							render={({ field }) => (
								<Textarea
									{...field}
									label={t("partners.domains")}
									description={t("partners.domainsHint")}
									minRows={3}
								/>
							)}
						/>
					) : (
						<>
							<Controller
								name="isActive"
								control={control}
								render={({ field }) => (
									<Switch
										label={t("common.active")}
										checked={field.value}
										onChange={(event) =>
											field.onChange(event.currentTarget.checked)
										}
									/>
								)}
							/>
							<Text size="sm" fw={500}>
								{t("partners.domains")}
							</Text>
							{(editingRow?.domains ?? []).map((domain) => (
								<Group key={domain.id} justify="space-between">
									<Text size="sm">{displayPartnerDomain(domain.domain)}</Text>
									<Button
										size="xs"
										color="red"
										variant="light"
										type="button"
										onClick={() =>
											onRequestDeleteDomain(
												domain.id,
												displayPartnerDomain(domain.domain),
											)
										}
									>
										{t("common.delete")}
									</Button>
								</Group>
							))}
							<Group align="flex-end">
								<Controller
									name="domainDraft"
									control={control}
									render={({ field }) => (
										<TextInput
											{...field}
											style={{ flex: 1 }}
											label={t("partners.emailDomain")}
											placeholder="softserveinc.com"
										/>
									)}
								/>
								<Button
									type="button"
									disabled={toPartnerApiDomain(domainDraft).length < 3}
									loading={domainPending}
									onClick={() => {
										onAddDomain(toPartnerApiDomain(domainDraft));
										setValue("domainDraft", "");
									}}
								>
									{t("partners.addDomain")}
								</Button>
							</Group>
							<Button
								color="red"
								variant="light"
								type="button"
								onClick={() => {
									if (editingRow) {
										onRequestDeletePartner(editingRow.id, editingRow.name);
									}
								}}
							>
								{t("partners.deletePartner")}
							</Button>
						</>
					)}
					<Group justify="flex-end">
						<Button variant="default" type="button" onClick={onClose}>
							{t("common.cancel")}
						</Button>
						<Button loading={saving} type="submit">
							{t("common.save")}
						</Button>
					</Group>
				</Stack>
			</form>
		</Modal>
	);
}
